import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Trash2, ArrowRightLeft, Loader2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { createTransfer } from '../services/inventoryRegistries.services';
import { getCurrentInventory } from '../services/inventoryCurrent.services';
import { fetchLaundries } from '@/pages/parametrizacion/laundries/Services/laundries.services';

const TransferModal = ({ open, onOpenChange, idLavanderia, laundryName = '', onCreated }) => {
  const { toast } = useToast();
  const [laundries, setLaundries] = useState([]);
  const [loadingLaundries, setLoadingLaundries] = useState(false);
  const [currentStock, setCurrentStock] = useState([]);
  const [loadingStock, setLoadingStock] = useState(false);
  const [selectedLaundry, setSelectedLaundry] = useState('');
  const [items, setItems] = useState([{ id: 1, productoId: '', cantidad: '' }]);
  const [observaciones, setObservaciones] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Cargar lavanderías disponibles (excluyendo la actual)
  const loadLaundries = useCallback(async () => {
    setLoadingLaundries(true);
    try {
      const res = await fetchLaundries({ limit: 200, offset: 1, query: { estado: 'Activo' } });
      const all = Array.isArray(res?.data) ? res.data : [];
      // Excluir la lavandería actual
      const filtered = all.filter(l => (l._id || l.id) !== idLavanderia);
      setLaundries(filtered);
    } catch (e) {
      console.error('Error cargando lavanderías:', e);
      setLaundries([]);
    } finally {
      setLoadingLaundries(false);
    }
  }, [idLavanderia]);

  // Cargar inventario actual del centro de lavado origen
  const loadStock = useCallback(async () => {
    if (!idLavanderia) return;
    setLoadingStock(true);
    try {
      const res = await getCurrentInventory({ lavanderiaId: idLavanderia });
      const data = Array.isArray(res?.data) ? res.data : [];
      setCurrentStock(data);
    } catch (e) {
      console.error('Error cargando stock:', e);
      setCurrentStock([]);
    } finally {
      setLoadingStock(false);
    }
  }, [idLavanderia]);

  useEffect(() => {
    if (open) {
      loadLaundries();
      loadStock();
    } else {
      setSelectedLaundry('');
      setItems([{ id: 1, productoId: '', cantidad: '' }]);
      setObservaciones('');
      setSubmitting(false);
    }
  }, [open, loadLaundries, loadStock]);

  // Productos disponibles con stock > 0
  const productOptions = useMemo(() => {
    return currentStock
      .filter(s => Number(s?.stockActual ?? 0) > 0)
      .map(s => ({
        id: s?.producto?.id || s?.producto?._id,
        nombre: s?.producto?.nombre || 'Producto',
        stockActual: Number(s?.stockActual ?? 0),
        costoUnitario: Number(s?.costoUnitario ?? 0),
      }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [currentStock]);

  const getProduct = (pid) => productOptions.find(p => p.id === pid) || null;

  // Lavandería destino seleccionada
  const selectedLaundryObj = useMemo(() => {
    return laundries.find(l => (l._id || l.id) === selectedLaundry) || null;
  }, [laundries, selectedLaundry]);

  const addRow = () => {
    setItems(prev => [...prev, { id: (prev[prev.length - 1]?.id || 0) + 1, productoId: '', cantidad: '' }]);
  };

  const removeRow = (id) => {
    setItems(prev => (prev.length > 1 ? prev.filter(it => it.id !== id) : prev));
  };

  const updateRow = (id, patch) => {
    setItems(prev => prev.map(it => (it.id === id ? { ...it, ...patch } : it)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLaundry || !selectedLaundryObj) {
      toast({ title: 'Centro requerido', description: 'Selecciona un centro de lavado destino.', variant: 'destructive' });
      return;
    }
    const valid = items.filter(it => {
      if (!it.productoId || !it.cantidad || Number(it.cantidad) <= 0) return false;
      const prod = getProduct(it.productoId);
      if (!prod) return false;
      if (Number(it.cantidad) > prod.stockActual) return false;
      return true;
    });
    if (valid.length === 0) {
      toast({ title: 'Productos requeridos', description: 'Agrega al menos un producto con cantidad válida (no mayor al stock disponible).', variant: 'destructive' });
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        lavanderiaOrigen: { id: idLavanderia, nombre: laundryName || 'Centro de lavado' },
        lavanderiaDestino: { id: selectedLaundryObj._id || selectedLaundryObj.id, nombre: selectedLaundryObj.nombre },
        items: valid.map(it => {
          const prod = getProduct(it.productoId);
          return {
            producto: { id: it.productoId, nombre: prod?.nombre || '' },
            cantidad: Number(it.cantidad),
            costoUnitario: prod?.costoUnitario || undefined,
          };
        }),
        observaciones: observaciones || undefined,
        fecha: new Date().toISOString(),
      };
      await createTransfer(payload);
      toast({ title: 'Traslado creado', description: `${valid.length} producto(s) trasladados a ${selectedLaundryObj.nombre}` });
      onOpenChange(false);
      onCreated && onCreated();
    } catch (error) {
      console.error('Error creando traslado:', error);
      const message = error?.response?.data?.message || error.message || 'No se pudo crear el traslado.';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-blue-600" />
            Crear traslado de inventario
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto space-y-5 pr-2">
            {/* Origen */}
            <div className="rounded-lg border p-3 bg-muted/30">
              <p className="text-sm font-medium mb-1">Centro de lavado origen</p>
              <p className="text-base font-semibold">{laundryName || 'Centro actual'}</p>
            </div>

            {/* Destino */}
            <div>
              <label className="block text-sm font-medium mb-1">Centro de lavado destino</label>
              <select
                value={selectedLaundry}
                onChange={(e) => setSelectedLaundry(e.target.value)}
                className="w-full border rounded-md p-2 h-10 bg-background text-foreground border-input focus:border-ring focus:ring-1 focus:ring-ring"
                disabled={loadingLaundries}
                required
              >
                <option value="" disabled>
                  {loadingLaundries ? 'Cargando centros...' : 'Selecciona un centro de lavado'}
                </option>
                {laundries.map(l => (
                  <option key={l._id || l.id} value={l._id || l.id}>
                    {l.nombre} {l.ciudad ? `- ${l.ciudad}` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Productos a trasladar */}
            <div>
              <p className="text-sm font-medium mb-2">Productos a trasladar</p>
              {loadingStock ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                  <Loader2 className="h-4 w-4 animate-spin" /> Cargando inventario...
                </div>
              ) : productOptions.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">No hay productos con stock disponible.</p>
              ) : (
                <div className="space-y-3">
                  {items.map((row) => {
                    const prod = getProduct(row.productoId);
                    const qty = Number(row.cantidad) || 0;
                    const exceedsStock = prod && qty > prod.stockActual;
                    return (
                      <div key={row.id} className="rounded-lg border p-3 bg-card">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
                          <div className="md:col-span-5">
                            <label className="block text-sm font-medium mb-1">Producto</label>
                            <select
                              value={row.productoId}
                              onChange={(e) => updateRow(row.id, { productoId: e.target.value, cantidad: '' })}
                              className="w-full border rounded-md p-2 h-10 bg-background text-foreground border-input focus:border-ring focus:ring-1 focus:ring-ring"
                              required
                            >
                              <option value="" disabled>Selecciona un producto</option>
                              {productOptions.map(p => (
                                <option key={p.id} value={p.id}>
                                  {p.nombre} (Stock: {p.stockActual})
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium mb-1">Stock actual</label>
                            <div className="h-10 flex items-center px-3 border rounded-md text-sm bg-muted/50">
                              {prod ? prod.stockActual : '-'}
                            </div>
                          </div>
                          <div className="md:col-span-3">
                            <label className="block text-sm font-medium mb-1">Cantidad a trasladar</label>
                            <Input
                              type="number"
                              value={row.cantidad}
                              onChange={(e) => updateRow(row.id, { cantidad: e.target.value })}
                              min="0"
                              max={prod?.stockActual || undefined}
                              step="0.01"
                              required
                              className={`h-10 ${exceedsStock ? 'border-red-500' : ''}`}
                            />
                            {exceedsStock && (
                              <p className="text-xs text-red-500 mt-1">Excede el stock disponible</p>
                            )}
                          </div>
                          <div className="md:col-span-2 flex items-end gap-1">
                            <div>
                              <label className="block text-sm font-medium mb-1 opacity-0 select-none">Acc</label>
                              <div className="h-10 flex items-center gap-1">
                                <Button type="button" variant="outline" size="icon" onClick={addRow} aria-label="Agregar línea" className="shrink-0">
                                  <Plus className="h-4 w-4" />
                                </Button>
                                <Button type="button" variant="ghost" size="icon" onClick={() => removeRow(row.id)} disabled={items.length <= 1} aria-label="Quitar línea" className="shrink-0">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Observaciones */}
            <div>
              <label className="block text-sm font-medium mb-1">Observaciones</label>
              <textarea
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                placeholder="Opcional"
                className="w-full border rounded-md p-2 min-h-[80px] bg-background text-foreground border-input focus:border-ring focus:ring-1 focus:ring-ring"
              />
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting || loadingStock || productOptions.length === 0}>
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Trasladando...</>
              ) : (
                'Crear traslado'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TransferModal;
